/**
 * Newsfeed `created_by` is either a user id to resolve to a name, or a channel
 * sentinel like `"buffer:facebook"` written by a sync. Classifying it before
 * anything is looked up keeps a non-user author out of the users endpoint,
 * which 400s on anything non-numeric.
 */

export const BUFFER_LABEL = "BUFFER";

export type Author =
  | { kind: "user"; userId: string }
  /** A sync source rather than a person: render the label, look nothing up. */
  | { kind: "source"; label: string }
  | { kind: "unknown" };

/** Everything before the colon in a sentinel, e.g. `buffer:facebook`. */
const SENTINEL = /^([a-z][a-z0-9_-]*):/i;

const SOURCE_LABELS: Record<string, string> = {
  buffer: BUFFER_LABEL,
};

export function describeAuthor(createdBy: unknown): Author {
  if (typeof createdBy === "number" && Number.isFinite(createdBy)) {
    return { kind: "user", userId: String(createdBy) };
  }
  if (typeof createdBy !== "string") return { kind: "unknown" };

  const value = createdBy.trim();
  if (value === "") return { kind: "unknown" };

  const sentinel = SENTINEL.exec(value);
  if (sentinel) {
    const scheme = sentinel[1].toLowerCase();
    // An unrecognised sentinel is still not a user id: label it by scheme.
    return { kind: "source", label: SOURCE_LABELS[scheme] ?? scheme.toUpperCase() };
  }

  // The Auth service takes a numeric id; anything else 400s.
  return /^\d+$/.test(value) ? { kind: "user", userId: value } : { kind: "unknown" };
}

/** The ids the list actually needs to resolve, deduplicated. */
export function userIdsToResolve(rows: readonly { created_by?: unknown }[]): string[] {
  const ids = new Set<string>();
  for (const row of rows) {
    const author = describeAuthor(row.created_by);
    if (author.kind === "user") ids.add(author.userId);
  }
  return [...ids];
}

/** What the Created By cell shows, given whatever names have resolved so far. */
export function authorLabel(createdBy: unknown, names: Record<string, string>): string {
  const author = describeAuthor(createdBy);
  switch (author.kind) {
    case "source":
      return author.label;
    case "user":
      return names[author.userId] ?? "N.A";
    default:
      return "N.A";
  }
}
